import Alpine from 'alpinejs'

import type { TFilterStore } from './stores/filterStore'

type TFilterOption = {
    key: string | number
    count?: number
    filters?: TFilterDefinition[]
}

type TFilterDefinition = {
    key: string
    type: string
    options?: TFilterOption[]
    min?: number
    max?: number
}

const API_BASE = 'https://api.datamotive.nl/api/v1'

const PRICE_BRACKETS = [5000, 10000, 15000, 20000, 25000, 30000, 40000, 50000, 75000, 100000, 150000, 200000]

export const initFilterBlock = (): void => {
    Alpine.data('filterBlock', () => ({
        listId: '',
        filterId: '',
        resultsUrl: '',
        loading: false,
        loaded: false,
        count: 0,
        controller: null as AbortController | null,
        filters: [] as TFilterDefinition[],
        selected: {
            vehicleState: [] as string[],
            brand: '',
            model: '',
            fuel: '',
            yearFrom: '',
            yearTo: '',
            priceMax: '',
        },

        init() {
            const el = this.$el as HTMLElement
            this.listId = el.dataset.vehicleListId || ''
            this.filterId = el.dataset.vehicleFilterListId || ''
            this.resultsUrl = el.dataset.resultsUrl || ''
            this.load()

            window.addEventListener('pageshow', (event: PageTransitionEvent) => {
                if (event.persisted) this.reset()
            })
        },

        reset() {
            this.selected = {
                vehicleState: [],
                brand: '',
                model: '',
                fuel: '',
                yearFrom: '',
                yearTo: '',
                priceMax: '',
            }
            this.load()
        },

        t(key: string | number): string {
            const store = Alpine.store('filterStore') as TFilterStore | undefined
            return store?.t ? store.t(String(key)) : String(key)
        },

        findFilter(key: string): TFilterDefinition | undefined {
            return this.filters.find((f) => f.key === key)
        },

        get brandOptions(): TFilterOption[] {
            return this.findFilter('brand')?.options ?? []
        },

        get fuelOptions(): TFilterOption[] {
            return this.findFilter('fuel')?.options ?? []
        },

        get modelOptions(): TFilterOption[] {
            if (!this.selected.brand) return []
            const brand = this.brandOptions.find((b) => String(b.key) === this.selected.brand)
            const modelFilter = brand?.filters?.find((f) => f.key === 'model')
            return modelFilter?.options ?? []
        },

        get yearOptions(): number[] {
            const years = new Set<number>()
            for (const option of this.findFilter('yearOfConstruction')?.options ?? []) {
                const year = Number(option.key)
                if (Number.isFinite(year)) years.add(year)
            }
            for (const selected of [this.selected.yearFrom, this.selected.yearTo]) {
                if (selected) years.add(Number(selected))
            }
            return [...years].sort((a, b) => b - a)
        },

        get priceBrackets(): number[] {
            return PRICE_BRACKETS
        },

        formatPrice(value: number): string {
            return new Intl.NumberFormat('nl-NL', {
                style: 'currency',
                currency: 'EUR',
                maximumFractionDigits: 0,
            }).format(value)
        },

        get submitDisabled(): boolean {
            return this.loading || (this.loaded && this.count === 0)
        },

        filterParts(): string[] {
            const parts: string[] = []
            const { vehicleState, brand, model, fuel, yearFrom, yearTo, priceMax } = this.selected

            vehicleState.forEach((state) => parts.push(`vehicleState[${state}]=`))

            if (brand) {
                parts.push(`brand[${encodeURIComponent(brand)}]=`)
                if (model) {
                    parts.push(`brand[${encodeURIComponent(brand)}][model][${encodeURIComponent(model)}]=`)
                }
            }

            if (fuel) parts.push(`fuel[${fuel}]=`)
            if (yearFrom) parts.push(`yearOfConstruction[min][${yearFrom}]=`)
            if (yearTo) parts.push(`yearOfConstruction[max][${yearTo}]=`)
            if (priceMax) parts.push(`price[max][${priceMax}]=`)

            return parts
        },

        countQuery(): string {
            return ['page=1', 'pageSize=1', ...this.filterParts()].join('&')
        },

        submitQuery(): string {
            return ['pageSize=12', 'sort=created_at_desc', 'page=1', ...this.filterParts()].join('&')
        },

        refresh() {
            this.load()
        },

        onBrandChange() {
            this.selected.model = ''
            this.refresh()
        },

        async load() {
            if (!this.listId || !this.filterId) return

            try {
                this.loading = true

                if (this.controller) this.controller.abort()
                this.controller = new AbortController()

                const response = await fetch(
                    `${API_BASE}/stock/vehicle-list/${this.listId}/vehicles?filterId=${this.filterId}&${this.countQuery()}` +
                        '&attributes[]=yearOfConstruction&attributes[]=fuel',
                    {
                        signal: this.controller.signal,
                        method: 'GET',
                        headers: {
                            'Accept-Language': document.documentElement.lang || 'nl',
                        },
                    }
                )

                if (!response.ok) return

                const data = await response.json()
                this.count = data.count ?? 0
                this.filters = data.filters ?? []
                this.loaded = true
            } catch (err: any) {
                if (err?.name !== 'AbortError') {
                    console.error(err)
                }
            } finally {
                this.controller = null
                this.loading = false
            }
        },

        submit() {
            if (this.submitDisabled) return
            const base = this.resultsUrl || '/'
            window.location.href = `${base}?${this.submitQuery()}`
        },
    }))
}
